import { useState, useEffect, useCallback } from 'react';

/**
 * Hook pour gérer les nouveaux projets et leur affichage avec indicateur "Nouveau"
 */
export const useNewProjectsIndicator = () => {
  const [newProjectIds, setNewProjectIds] = useState(new Set());
  const [lastViewedTime, setLastViewedTime] = useState(null);

  // Charger les données de localStorage au montage
  useEffect(() => {
    const savedNewProjects = localStorage.getItem('new_project_ids');
    const savedLastViewedTime = localStorage.getItem('last_projects_view_time');
    
    if (savedNewProjects) {
      try {
        const parsedIds = JSON.parse(savedNewProjects);
        setNewProjectIds(new Set(parsedIds));
      } catch (error) {
        console.warn('Erreur lors du chargement des nouveaux projets:', error);
      }
    }
    
    if (savedLastViewedTime) {
      setLastViewedTime(new Date(savedLastViewedTime));
    } else {
      // Première visite, définir la date actuelle
      const now = new Date();
      setLastViewedTime(now);
      localStorage.setItem('last_projects_view_time', now.toISOString());
    }
  }, []);

  // Sauvegarder les changements dans localStorage
  useEffect(() => {
    if (newProjectIds.size > 0) {
      localStorage.setItem('new_project_ids', JSON.stringify([...newProjectIds]));
    }
  }, [newProjectIds]);

  /**
   * Marquer un projet comme nouveau
   * @param {string} projectId - ID du projet
   */
  const markProjectAsNew = useCallback((projectId) => {
    setNewProjectIds(prev => new Set([...prev, projectId]));
  }, []);

  /**
   * Marquer un projet comme vu (retirer l'indicateur "nouveau")
   * @param {string} projectId - ID du projet
   */
  const markProjectAsViewed = useCallback((projectId) => {
    setNewProjectIds(prev => {
      const updated = new Set(prev);
      updated.delete(projectId);
      return updated;
    });
  }, []);

  /**
   * Marquer tous les projets comme vus
   */
  const markAllProjectsAsViewed = useCallback(() => {
    setNewProjectIds(new Set());
    localStorage.removeItem('new_project_ids');
    
    const now = new Date();
    setLastViewedTime(now);
    localStorage.setItem('last_projects_view_time', now.toISOString());
  }, []);

  /**
   * Vérifier si un projet est nouveau
   * @param {string} projectId - ID du projet
   * @returns {boolean}
   */
  const isProjectNew = useCallback((projectId) => {
    return newProjectIds.has(projectId);
  }, [newProjectIds]);

  /**
   * Vérifier si un projet doit être marqué comme nouveau basé sur sa date de création
   * @param {object} project - Objet projet avec dateCreated
   * @returns {boolean}
   */
  const shouldMarkAsNew = useCallback((project) => {
    if (!project.dateCreated || !lastViewedTime) return false;
    
    const projectDate = new Date(project.dateCreated);
    const daysDiff = (Date.now() - projectDate.getTime()) / (1000 * 60 * 60 * 24);
    
    // Marquer comme nouveau si:
    // 1. Le projet a été créé dans les 7 derniers jours
    // 2. Le projet a été créé après la dernière visite de l'utilisateur
    return daysDiff <= 7 && projectDate > lastViewedTime;
  }, [lastViewedTime]);

  /**
   * Traiter une liste de projets pour marquer les nouveaux automatiquement
   * @param {array} projects - Liste des projets
   * @returns {array} - Projets avec la propriété isNew ajoutée
   */
  const processProjectsForNewIndicator = useCallback((projects) => {
    return projects.map(project => {
      const isNew = isProjectNew(project.id) || shouldMarkAsNew(project);
      
      // Marquer automatiquement comme nouveau si nécessaire
      if (shouldMarkAsNew(project) && !isProjectNew(project.id)) {
        markProjectAsNew(project.id);
      }
      
      return {
        ...project,
        isNew,
        isRecentlyAdded: shouldMarkAsNew(project)
      };
    });
  }, [isProjectNew, shouldMarkAsNew, markProjectAsNew]);

  /**
   * Obtenir le nombre de nouveaux projets
   * @returns {number}
   */
  const getNewProjectsCount = useCallback(() => {
    return newProjectIds.size;
  }, [newProjectIds]);

  /**
   * Nettoyer les anciens projets (plus de 30 jours)
   */
  const cleanupOldNewProjects = useCallback((projects) => {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    
    setNewProjectIds(prev => {
      const updated = new Set();
      prev.forEach(projectId => {
        const project = projects.find(p => p.id === projectId);
        if (project && project.dateCreated) {
          const projectDate = new Date(project.dateCreated);
          if (projectDate > thirtyDaysAgo) {
            updated.add(projectId);
          }
        }
      });
      // Conserver la même référence si rien n'a changé (évite des re-rendus en cascade)
      return updated.size === prev.size ? prev : updated;
    });
  }, []);

  return {
    // États
    newProjectIds: [...newProjectIds],
    lastViewedTime,
    
    // Actions
    markProjectAsNew,
    markProjectAsViewed,
    markAllProjectsAsViewed,
    
    // Vérifications
    isProjectNew,
    shouldMarkAsNew,
    getNewProjectsCount,
    
    // Traitement
    processProjectsForNewIndicator,
    cleanupOldNewProjects
  };
};

export default useNewProjectsIndicator;